import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useTranslation } from "react-i18next";
import { kioskClient } from "../../lib/kiosk-client";
import type { FlowState } from "../../pages/FlowPage";
import { BigButton } from "../BigButton";

interface Props {
  state: FlowState;
  onNext: (update: Partial<FlowState>) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

const POLL_INTERVAL_MS = 3000;
const SESSION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export function ProcessingStep({ state, onNext, onError, onCancel }: Props) {
  const { t } = useTranslation();
  const submitted = useRef(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(state.order_id ?? null);
  const [phase, setPhase] = useState<"creating" | "qr" | "paid" | "timeout">(
    "creating",
  );
  const [secondsLeft, setSecondsLeft] = useState(SESSION_TIMEOUT_MS / 1000);

  // Countdown ticker
  useEffect(() => {
    if (phase !== "qr") return;
    const tick = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [phase]);

  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;

    async function init() {
      const { amount_cents, department_id, external_reference } = state;
      if (!amount_cents) {
        onError(t("error.networkBody"));
        return;
      }

      try {
        const featurePath = state.feature_path;
        if (!featurePath) {
          onError(t("error.networkBody"));
          return;
        }

        // Step 1: create order if not already created.
        let oid = orderId;
        if (!oid) {
          const { data: order } = await kioskClient.createOrder({
            amount_cents,
            ...(department_id ? { department_id } : {}),
            ...(external_reference ? { external_reference } : {}),
          });
          oid = order.order_id;
          setOrderId(oid);
        }

        // Step 2: mint checkout URL for QR.
        const { data: link } = await kioskClient.createPaymentLink(
          oid,
          featurePath,
        );
        setCheckoutUrl(link.checkout_url);
        setPhase("qr");

        // Step 3: poll order status.
        pollTimer.current = setInterval(async () => {
          try {
            const { data: order } = await kioskClient.getOrder(oid!);
            if (order.status === "paid") {
              clearInterval(pollTimer.current!);
              clearTimeout(timeoutTimer.current!);
              setPhase("paid");
              setTimeout(() => {
                onNext({
                  order_id: oid!,
                  order_status: "paid",
                  payment_success: true,
                });
              }, 1200);
            }
          } catch {
            // transient — keep polling
          }
        }, POLL_INTERVAL_MS);

        // Step 4: timeout after 5 minutes.
        timeoutTimer.current = setTimeout(() => {
          clearInterval(pollTimer.current!);
          setPhase("timeout");
        }, SESSION_TIMEOUT_MS);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t("error.networkBody");
        onError(msg);
      }
    }

    void init();

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- "creating order" phase ----
  if (phase === "creating") {
    return (
      <div className="flex flex-col items-center justify-center gap-6 h-full px-8">
        <div
          className="w-16 h-16 rounded-full border-8 border-[var(--kiosk-brand-accent)] border-t-transparent animate-spin"
          role="status"
          aria-label={t("flow.processing.pleaseWait")}
        />
        <p className="text-2xl font-semibold text-center">
          {t("flow.processing.preparing")}
        </p>
      </div>
    );
  }

  // ---- session timed out ----
  if (phase === "timeout") {
    return (
      <div className="flex flex-col items-center justify-center gap-6 h-full px-8">
        <div className="text-6xl">⏰</div>
        <p className="text-2xl font-semibold text-center">
          {t("flow.processing.timeout")}
        </p>
        <BigButton variant="secondary" onClick={onCancel}>
          {t("flow.processing.startOver")}
        </BigButton>
      </div>
    );
  }

  // ---- payment confirmed ----
  if (phase === "paid") {
    return (
      <div className="flex flex-col items-center justify-center gap-6 h-full px-8">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-10 h-10 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <p className="text-2xl font-semibold text-green-700">
          {t("flow.processing.paymentReceived")}
        </p>
      </div>
    );
  }

  // ---- QR code display ----
  const minutes = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, "0");
  const amountDisplay =
    state.amount_cents != null
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(state.amount_cents / 100)
      : "";

  return (
    <div className="flex flex-col h-full">
      {/* Top banner */}
      <div className="bg-[var(--kiosk-brand-accent)] text-white text-center py-4 px-6">
        <p className="text-lg font-semibold">
          {t("flow.processing.scanToPayTitle")}
        </p>
        {amountDisplay && (
          <p className="text-4xl font-bold mt-1">{amountDisplay}</p>
        )}
      </div>

      {/* QR code */}
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 pb-4">
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <QRCode
            value={checkoutUrl ?? ""}
            size={220}
            level="M"
            style={{ display: "block" }}
          />
        </div>

        <div className="text-center space-y-2">
          <p className="text-xl font-semibold text-[var(--kiosk-brand-text)]">
            {t("flow.processing.scanWithPhone")}
          </p>
          <p className="text-[var(--kiosk-brand-muted)] text-base">
            {t("flow.processing.completeOnPhone")}
          </p>
        </div>

        {/* Countdown + pulsing indicator */}
        <div className="flex items-center gap-3 text-[var(--kiosk-brand-muted)]">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--kiosk-brand-accent)] animate-pulse" />
          <span className="text-base">
            {t("flow.processing.timeRemaining", {
              time: `${minutes}:${secs}`,
            })}
          </span>
        </div>
      </div>

      {/* Cancel */}
      <div className="px-8 pb-8">
        <BigButton variant="secondary" fullWidth onClick={onCancel}>
          {t("flow.cancel")}
        </BigButton>
      </div>
    </div>
  );
}
